import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { ProductTable } from '@components/molecules/ProductTable';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { ShoppingCartTotalData } from '@types';
import { SITE_URLS } from '@constants';
import { useClearCart } from '@services/useClearCart';
import { getProductAvailability } from '@utils/products';
import { useData } from '@hooks/useData';
import { Notification } from '@components/molecules/Notification';

const CURRENT_STEP = 1;

export interface ShoppingCartProps {
    productsByMerchant: {
        merchant?: string;
        cartItems?: {
            uuid?: string;
            product?: {
                uuid?: string;
                name?: string;
                image?: string;
            };
            price?: number;
            availability?: string;
            quantity?: number;
        }[];
    }[];
    hasOutOfStockItem: boolean;
    total: Omit<ShoppingCartTotalData, 'products'>;
}

export const ShoppingCart: FC<ShoppingCartProps> = ({ productsByMerchant, hasOutOfStockItem, total }) => {
    const { request: requestClearCart, loading: loadingClearCart } = useClearCart();
    const { login, cart } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('shopping-cart-component.title'),
        subtitle: t('shopping-cart-component.subtitle'),
        emptyBasketLabel: t('shopping-cart-component.empty-basket.label'),
        proceedOrderButton: t('shopping-cart-component.proceed-order.button'),
        continueShoppingButton: t('shopping-cart-component.continue-shopping.button'),
        hasOutOfStockItem: t('shopping-cart-component.has-out-of-stock-item'),
    };

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Anchor
                        href={login ? SITE_URLS.orderBilling : SITE_URLS.login}
                        mode={AnchorMode.primary}
                        disabled={
                            hasOutOfStockItem || productsByMerchant.length === 0 || cart?.data?.promotion?.isValidCode === false
                        }>
                        {translations.proceedOrderButton}
                    </Anchor>

                    <Anchor href={SITE_URLS.products} mode={AnchorMode.primary} outline={true}>
                        {translations.continueShoppingButton}
                    </Anchor>
                </div>
            }
            total={{ ...total, products: [] }}>
            <>
                <div className="d-flex align-items-center gap-4 justify-content-between justify-content-lg-start">
                    <Typography classes="mb-0" fontSize="20">
                        <>
                            {`${translations.subtitle} - `}
                            <Typography tagVariant={TypographyTagVariants.span} fontWeight="800">
                                {`${total.nProducts}`}
                            </Typography>
                        </>
                    </Typography>
                    {productsByMerchant.length > 0 && (
                        <Button
                            mode={ButtonMode.danger}
                            label={translations.emptyBasketLabel}
                            size="sm"
                            icon="fa-light fa-trash-can"
                            disabled={loadingClearCart}
                            onClick={requestClearCart}
                        />
                    )}
                </div>

                {hasOutOfStockItem ? (
                    <Notification
                        type="error"
                        message={translations.hasOutOfStockItem}
                        borderRadiusStyle="rounded"
                        size="full-width"
                    />
                ) : (
                    <></>
                )}

                {productsByMerchant.map((item, index) => (
                    <ProductTable
                        key={`merchant-${index}`}
                        merchant={item.merchant}
                        cartItems={
                            item.cartItems?.map((item) => {
                                return {
                                    ...item,
                                    availability: getProductAvailability(item.availability),
                                };
                            }) || []
                        }
                    />
                ))}
            </>
        </ShoppingCartContainer>
    );
};
